import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import useSmartBack from "./smartBack";

const DEFAULT_HIDE_PATHS = [
  "/dashboard",
  "/",
  "/login",
  "/register",
  "/reset-password",
  "/reports/payroll",
  "/reports/esi",
  "/reports/payroll/employee-salary-register",
];

function normalizePath(p) {
  const s = (p || "/").trim();
  const noTrail = s.length > 1 ? s.replace(/\/+$/, "") : s;
  return noTrail || "/";
}

function shouldHideBack(pathname, search = "") {
  const p = normalizePath(pathname).toLowerCase();
  if (DEFAULT_HIDE_PATHS.map((x) => normalizePath(x).toLowerCase()).includes(p)) return true;
  if (p.startsWith("/reports/") || p === "/bank/reports") return true;
  const inPipeline = new URLSearchParams(search).has("pipeline");
  if (inPipeline && (p === "/salary-v2/structures" || p === "/salary-v2/formula-builder")) return true;
  return false;
}

function isEditableTarget(el) {
  if (!el || !(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return Boolean(el.isContentEditable);
}

/**
 * The one page-level Back button (rendered by Layout above every page).
 * Goes to the previous in-app page; with no history it opens the page's parent.
 */
export default function BackButton({
  fallbackPath,
  hidePaths = DEFAULT_HIDE_PATHS,
  showLabel = true,
}) {
  const location = useLocation();
  const onBack = useSmartBack(fallbackPath);

  const normalizedPathname = normalizePath(location.pathname);
  const hideSet = useMemo(() => {
    return new Set((hidePaths || []).map((p) => normalizePath(p).toLowerCase()));
  }, [hidePaths]);
  const hidden = shouldHideBack(normalizedPathname, location.search) || hideSet.has(normalizedPathname.toLowerCase());

  useEffect(() => {
    const onKey = (e) => {
      if (hidden) return;
      if (!e.altKey || e.key !== "ArrowLeft" || e.repeat) return;
      if (isEditableTarget(document.activeElement)) return;
      e.preventDefault();
      onBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack, hidden]);

  if (hidden) return null;

  return (
    <div className="page-back-bar" key={normalizedPathname}>
      <button type="button" className="page-back-btn" onClick={onBack} title="Go Back" aria-label="Go Back">
        <ArrowLeft size={16} strokeWidth={2} aria-hidden />
        {showLabel ? <span>Back</span> : null}
      </button>
    </div>
  );
}

export { DEFAULT_HIDE_PATHS };
